<!-- 'loading' → spinner; 'denied' → 404; 'allowed' → showcase. -->
@switch (gateState) {
  @case ('loading') {
    <div class="loading-state">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
      </div>
      <div class="loading-text">Loading test environment...</div>
    </div>
  }
  @case ('denied') {
    <arpio-center-panel>
      <h3 class="panel-title">Page Not Found</h3>
      <div class="margin-top">The page you're looking for doesn't exist or may have been moved.</div>
      <div class="margin-top actions">
        <a class="primary-button" href="/">
          <i class="fa-utility fa-semibold fa-arrow-left"></i>
          Back to home
        </a>
      </div>
    </arpio-center-panel>
  }
  @case ('allowed') {
    <ng-container *ngTemplateOutlet="testPageBody"></ng-container>
  }
}

<ng-template #testPageBody>
  <div class="test-page">
    <!-- Header -->
    <header class="page-header">
      <div class="header-content">
        <div class="header-title-row">
          <h1>Internal Test Page</h1>
          <button class="logout-btn" (click)="doLogout()">
            <i class="fa-solid fa-arrow-right-from-bracket"></i> Logout
          </button>
        </div>
        <p class="header-subtitle">Development environment for testing UI components with mock data.</p>
      </div>
    </header>

    <!-- Sticky section-nav — pinned to top on scroll, sibling of .page-header so it -->
    <!-- doesn't unstick when the header scrolls away. -->
    <nav class="section-nav">
      <span class="section-nav-label">Jump to section</span>
      <a class="nav-pill" (click)="scrollTo('accounts-section', 'accounts')">
        <i class="fa-solid fa-users"></i> Accounts
      </a>
      <a class="nav-pill" (click)="scrollTo('issues-section', 'issues')">
        <i class="fa-solid fa-circle-exclamation"></i> Issue Types
      </a>
      <a class="nav-pill" (click)="scrollTo('issues-scenarios-section', 'issuesScenarios')">
        <i class="fa-solid fa-vial-circle-check"></i> Scenarios
      </a>
      <a class="nav-pill" (click)="scrollTo('resources-section', 'resources')">
        <i class="fa-solid fa-cube"></i> Resources
      </a>
      <a class="nav-pill" (click)="scrollTo('recovery-screen-section', 'recoveryScreen')">
        <i class="fa-solid fa-display"></i> Recovery
      </a>
      <a class="nav-pill" (click)="scrollTo('backup-screen-section', 'backupScreen')">
        <i class="fa-solid fa-cloud-arrow-up"></i> Backup
      </a>
      <a class="nav-pill" (click)="scrollTo('tab-status-section', 'tabStatus')">
        <i class="fa-solid fa-bars-progress"></i> Tab Status
      </a>
      <a class="nav-pill" (click)="scrollTo('modals-section', 'modals')">
        <i class="fa-solid fa-window-restore"></i> Modals
      </a>
      <a class="nav-pill" (click)="scrollTo('chart-section', 'chart')">
        <i class="fa-solid fa-chart-line"></i> Chart
      </a>
      <a class="nav-pill" (click)="scrollTo('stacksets-section', 'stacksets')">
        <i class="fa-solid fa-layer-group"></i> StackSets
      </a>
    </nav>

    <!-- Accounts Section -->
    <section id="accounts-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('accounts')">
        <h2>
          <i class="fa-solid fa-users section-icon"></i>
          Accounts
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['accounts']"
          [class.fa-chevron-up]="expandedSections['accounts']"
        ></i>
      </div>
      @if (expandedSections['accounts']) {
        <div class="section-body">
          @if (accounts) {
            <div class="accounts-grid">
              @for (account of visibleAccounts; track account) {
                <div class="account-card">
                  <div class="account-id">
                    <span class="account-id-text">{{ account.accountId }}</span>
                    <a class="copy-clipboard" [copy-clipboard]="account.accountId">
                      <i class="fa-utility fa-semibold fa-copy"></i>
                    </a>
                  </div>
                  @if (account.name) {
                    <div class="account-name">{{ account.name }}</div>
                  }
                  <div class="account-actions">
                    <a class="action-link" routerLink="/{{ account.accountId }}" target="_blank">
                      <i class="fa-solid fa-arrow-up-right-from-square"></i> Open
                    </a>
                    <a
                      class="action-link"
                      routerLink="/create_account/aws_accounts"
                      [queryParams]="{ createdAccountId: account.accountId, nativeAuthToken: 'fakeToken' }"
                    >
                      <i class="fa-solid fa-plus"></i> Create App
                    </a>
                  </div>
                </div>
              }
            </div>
            @if (hasMoreAccounts) {
              <button class="show-more-btn" (click)="showAllAccounts = !showAllAccounts; $event.stopPropagation()">
                <i
                  class="fa-solid"
                  [class.fa-chevron-down]="!showAllAccounts"
                  [class.fa-chevron-up]="showAllAccounts"
                ></i>
                {{ showAllAccounts ? 'Show fewer' : 'Show all ' + accounts.length + ' accounts' }}
              </button>
            }
          } @else {
            <div class="notice-info">
              <i class="fa-solid fa-circle-info"></i>
              <p>
                You're authenticated but not a member of any accounts yet. Mock-data sections (Modals, Chart, Resources)
                below still work — visit <a href="/">the root URL</a> to create or join an account.
              </p>
            </div>
          }
        </div>
      }
    </section>

    <!-- Issues Section -->
    <section id="issues-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('issues')">
        <h2>
          <i class="fa-solid fa-circle-exclamation section-icon"></i>
          All Issue Types
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['issues']"
          [class.fa-chevron-up]="expandedSections['issues']"
        ></i>
      </div>
      @if (expandedSections['issues']) {
        <div class="section-body">
          <p class="section-description">All mock issue types rendered with their respective components.</p>
          <!-- Several issue components dereference `application` in ngOnInit, so
          only mount issues-view once an application is actually loaded. -->
          @if (application) {
            <issues-view [issues]="issues" [sync-pair]="syncPair" [application]="application"></issues-view>
          } @else {
            <div class="notice-info">
              <i class="fa-solid fa-circle-info"></i>
              <p>
                No application is loaded for this account, so issue components can't render. Modals below are still
                available for visual testing.
              </p>
            </div>
          }
        </div>
      }
    </section>

    <!-- Issues Scenarios Section -->
    <!-- Targeted repros for the ARP-7238 Issues-screen fixes. Each scenario feeds
         a dedicated issues-view a hand-built data set that reproduces one bug
         condition, so the fix can be confirmed visually. -->
    <section id="issues-scenarios-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('issuesScenarios')">
        <h2>
          <i class="fa-solid fa-vial-circle-check section-icon"></i>
          Issues Scenarios
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['issuesScenarios']"
          [class.fa-chevron-up]="expandedSections['issuesScenarios']"
        ></i>
      </div>
      <div class="section-body" *ngIf="expandedSections['issuesScenarios']">
        <p class="section-description">
          Edge-case data sets that reproduce the ARP-7238 Issues-screen bugs, so the dedup and name-highlight fixes can
          be verified visually.
        </p>

        <ng-container *ngIf="application; else needAppForScenarios">
          <!-- Scenario 1 — duplicate rows -->
          <h3 class="scenario-heading">Duplicate resource rows</h3>
          <div class="notice-info">
            <i class="fa-solid fa-circle-info"></i>
            <p>
              The same resource (identical ARN) is raised three times for one problem. The backend appends issues
              without deduping, so pre-fix this listed the resource on three separate rows. The fix collapses same-ARN
              repeats within a sub-group, so it should now show a <strong>single</strong> row.
            </p>
          </div>
          <issues-view [issues]="duplicateRowsIssues" [sync-pair]="syncPair" [application]="application"></issues-view>

          <!-- Scenario 2 — tripled name -->
          <h3 class="scenario-heading">Identical names (tripled-name highlight)</h3>
          <div class="notice-info">
            <i class="fa-solid fa-circle-info"></i>
            <p>
              Two distinct resources (different ARNs) share one display name. The diff-highlight finds a common prefix
              and suffix that overlap on identical names, so pre-fix the name rendered concatenated/tripled. Dedup
              leaves both rows (ARNs differ); the highlight clamp should render each name
              <strong>once</strong>. Use the per-row <strong>ARN</strong> button to tell the two rows apart.
            </p>
          </div>
          <issues-view [issues]="tripledNameIssues" [sync-pair]="syncPair" [application]="application"></issues-view>
        </ng-container>
        <ng-template #needAppForScenarios>
          <div class="notice-info">
            <i class="fa-solid fa-circle-info"></i>
            <p>No application is loaded for this account, so the issues-view scenarios can't render.</p>
          </div>
        </ng-template>
      </div>
    </section>

    <!-- Modals Section -->
    <!-- Every modal in the app, openable with mock data. The actual modal
    components are used so chrome/typography stays in sync with prod. -->
    <section id="modals-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('modals')">
        <h2>
          <i class="fa-solid fa-window-restore section-icon"></i>
          Modals
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['modals']"
          [class.fa-chevron-up]="expandedSections['modals']"
        ></i>
      </div>
      @if (expandedSections['modals']) {
        <div class="section-body">
          <p class="section-description">
            Click any button to open the corresponding modal with mock data — for visual testing only. Buttons that need
            a loaded application are disabled until one is available.
          </p>
          <h3 class="modal-group-title">Recovery flow</h3>
          <div class="modal-buttons-grid">
            <button class="arpio-button" (click)="openLaunchRecovery()" [disabled]="!application">
              Launch recovery (AWS)
            </button>
            <button class="arpio-button" (click)="openLaunchRecoveryAzure()" [disabled]="!application">
              Launch recovery (Azure)
            </button>
            <button class="arpio-button" (click)="openLaunchTest()" [disabled]="!application">Launch test</button>
            <button class="arpio-button" (click)="openConcludeRecovery()" [disabled]="!application">
              Conclude recovery
            </button>
            <button class="arpio-button" (click)="openConcludeTest()" [disabled]="!application">Conclude test</button>
            <button class="arpio-button" (click)="openStopTest()" [disabled]="!application">Cancel test (early)</button>
            <button class="arpio-button" (click)="openFailback()" [disabled]="!application">Failback</button>
            <button class="arpio-button" (click)="openUnquarantine()" [disabled]="!application">
              Unquarantine (AWS)
            </button>
            <button class="arpio-button" (click)="openUnquarantineAzure()" [disabled]="!application">
              Unquarantine (Azure)
            </button>
            <button class="arpio-button" (click)="openAppTestConfig()" [disabled]="!application">
              Sandbox settings (basic)
            </button>
            <button class="arpio-button" (click)="openAppTestConfigAdvanced()" [disabled]="!application">
              Sandbox settings (advanced)
            </button>
            <button class="arpio-button" (click)="openSandboxEvents()" [disabled]="!application">
              Network sandbox events (empty)
            </button>
            <button class="arpio-button" (click)="openSandboxEventsPopulated()" [disabled]="!application">
              Network sandbox events (populated)
            </button>
            <button class="arpio-button" (click)="openAreYouSure()" [disabled]="!application">
              Recovery "Are you sure"
            </button>
          </div>
          <h3 class="modal-group-title">Network sandbox — customer configurations</h3>
          <p class="section-description">
            Each scenario fixes the cloud, recovery-point contents, and account capabilities that drive the sandbox UI's
            conditional logic, then opens the dialog in that one rendered state.
          </p>
          <div class="sandbox-scenario-pickers">
            <div class="sandbox-scenario-picker">
              <label class="recovery-screen-picker">
                <span class="recovery-screen-picker-label">Launch test dialog</span>
                <select class="arpio-select" (change)="launchTestSandboxKey = $any($event.target).value">
                  <optgroup *ngFor="let group of launchTestSandboxGroups" [label]="group.group">
                    <option
                      *ngFor="let scenario of group.scenarios"
                      [value]="scenario.key"
                      [selected]="scenario.key === launchTestSandboxKey"
                    >
                      {{ scenario.label }}
                    </option>
                  </optgroup>
                </select>
              </label>
              <button class="arpio-button" (click)="openLaunchTestSandboxScenario()" [disabled]="!application">
                Open
              </button>
            </div>
            <div class="sandbox-scenario-picker">
              <label class="recovery-screen-picker">
                <span class="recovery-screen-picker-label">Sandbox settings dialog</span>
                <select class="arpio-select" (change)="sandboxConfigKey = $any($event.target).value">
                  <optgroup *ngFor="let group of sandboxConfigGroups" [label]="group.group">
                    <option
                      *ngFor="let scenario of group.scenarios"
                      [value]="scenario.key"
                      [selected]="scenario.key === sandboxConfigKey"
                    >
                      {{ scenario.label }}
                    </option>
                  </optgroup>
                </select>
              </label>
              <button class="arpio-button" (click)="openSandboxConfigScenario()" [disabled]="!application">Open</button>
            </div>
          </div>
          <h3 class="modal-group-title">Generic / shared</h3>
          <div class="modal-buttons-grid">
            <button class="arpio-button" (click)="openConfirmAction()">Confirm action</button>
            <button class="arpio-button" (click)="openUpdateAccess()">Update access</button>
            <button class="arpio-button" (click)="openEditIntegration()">Edit integration</button>
          </div>
          <h3 class="modal-group-title">Support / retention / issues</h3>
          <div class="modal-buttons-grid">
            <button class="arpio-button" (click)="openSubmitTicket()">Submit ticket</button>
            <button class="arpio-button" (click)="openConfirmTicket()">Ticket opened (confirmation)</button>
            <button class="arpio-button" (click)="openReleaseRps()">Release recovery points</button>
            <button class="arpio-button" (click)="openConfirmManualReplication()">Confirm manual replication</button>
            <button class="arpio-button" (click)="openConfirmEbsSnapshot()">Confirm EBS snapshot replication</button>
          </div>
          <h3 class="modal-group-title">Users / roles / groups</h3>
          <div class="modal-buttons-grid">
            <button class="arpio-button" (click)="openInviteUsers()">Invite users</button>
            <button class="arpio-button" (click)="openEditUser()">Edit user</button>
            <button class="arpio-button" (click)="openNewRole()">New role</button>
            <button class="arpio-button" (click)="openEditRole()">Edit role</button>
            <button class="arpio-button" (click)="openNewGroup()">New group</button>
            <button class="arpio-button" (click)="openEditGroup()">Edit group</button>
            <button class="arpio-button" (click)="openIdpModal()">IdP</button>
          </div>
          <h3 class="modal-group-title">API keys</h3>
          <div class="modal-buttons-grid">
            <button class="arpio-button" (click)="openNewApiKey()">New API key</button>
            <button class="arpio-button" (click)="openEditApiKey()">Edit API key</button>
          </div>
          <!-- Template-based modals: each is hosted by a modal-dialog with an
          ng-template body that mounts the actual dialog component. -->
          @if (application) {
            <modal-dialog #launchRecoveryDialog [dialogTemplate]="launchRecoveryTpl"></modal-dialog>
            <ng-template #launchRecoveryTpl>
              <launch-recovery-dialog
                [application]="application"
                (cancel)="launchRecoveryDialog.close()"
                (launch)="launchRecoveryDialog.close()"
              ></launch-recovery-dialog>
            </ng-template>
            <modal-dialog #launchTestDialog [dialogTemplate]="launchTestTpl"></modal-dialog>
            <ng-template #launchTestTpl>
              <launch-test-dialog
                [application]="application"
                [currentlySyncing]="false"
                [launching]="mockLaunchTestLaunching"
                (cancel)="resetMockCapabilities(); launchTestDialog.close()"
                (launch)="mockLaunchTestLaunching = true"
              ></launch-test-dialog>
            </ng-template>
            <modal-dialog #concludeRecoveryDialog [dialogTemplate]="concludeRecoveryTpl"></modal-dialog>
            <ng-template #concludeRecoveryTpl>
              <conclude-recovery-dialog
                [application]="application"
                [currentlySyncing]="false"
                (cancel)="concludeRecoveryDialog.close()"
                (conclude)="concludeRecoveryDialog.close()"
              ></conclude-recovery-dialog>
            </ng-template>
            <modal-dialog #concludeTestDialog [dialogTemplate]="concludeTestTpl"></modal-dialog>
            <ng-template #concludeTestTpl>
              <conclude-test-dialog
                [application]="application"
                (cancel)="concludeTestDialog.close()"
                (conclude)="concludeTestDialog.close()"
              ></conclude-test-dialog>
            </ng-template>
            <modal-dialog #stopTestDialog [dialogTemplate]="stopTestTpl"></modal-dialog>
            <ng-template #stopTestTpl>
              <conclude-test-dialog
                [application]="application"
                [isEarlyStop]="true"
                (cancel)="stopTestDialog.close()"
                (conclude)="stopTestDialog.close()"
              ></conclude-test-dialog>
            </ng-template>
            <modal-dialog #failbackDialog [dialogTemplate]="failbackTpl"></modal-dialog>
            <ng-template #failbackTpl>
              <failback-dialog
                [application]="application"
                (cancel)="failbackDialog.close()"
                (failback)="failbackDialog.close()"
              ></failback-dialog>
            </ng-template>
            <modal-dialog #unquarantineDialog [dialogTemplate]="unquarantineTpl"></modal-dialog>
            <ng-template #unquarantineTpl>
              <unquarantine-dialog
                [application]="application"
                (close)="unquarantineDialog.close()"
              ></unquarantine-dialog>
            </ng-template>
            <modal-dialog #appTestConfigDialog [dialogTemplate]="appTestConfigTpl"></modal-dialog>
            <ng-template #appTestConfigTpl>
              <application-test-config-dialog
                [application]="application"
                [currentlySyncing]="false"
                (cancel)="resetMockCapabilities(); appTestConfigDialog.close()"
                (launch)="resetMockCapabilities(); appTestConfigDialog.close()"
              ></application-test-config-dialog>
            </ng-template>
            <modal-dialog #appTestConfigAdvancedDialog [dialogTemplate]="appTestConfigAdvancedTpl"></modal-dialog>
            <ng-template #appTestConfigAdvancedTpl>
              <application-test-config-dialog
                [application]="application"
                [currentlySyncing]="false"
                (cancel)="appTestConfigAdvancedDialog.close()"
                (launch)="appTestConfigAdvancedDialog.close()"
              ></application-test-config-dialog>
            </ng-template>
            <modal-dialog #awsEventLogsDialog [dialogTemplate]="awsEventLogsTpl"></modal-dialog>
            <ng-template #awsEventLogsTpl>
              <app-aws-event-logs
                [accountId]="application.accountId"
                [applicationId]="application.applicationId"
                (close)="awsEventLogsDialog.close()"
              ></app-aws-event-logs>
            </ng-template>
            <!-- Inline "Are you sure?" recovery confirm — copied verbatim from
            application.component.html so the test page can verify its
            close button + escape handler in isolation. -->
            <modal-dialog #areYouSureDialog [dialogTemplate]="areYouSureTpl"></modal-dialog>
            <ng-template #areYouSureTpl>
              <div class="flex-left-column light-control-host">
                <div class="modal-header">
                  <h3 class="panel-title">Launch recovery for these applications?</h3>
                  <button
                    type="button"
                    class="modal-close-btn"
                    (click)="areYouSureDialog.close()"
                    pTooltip="Close"
                    tooltipPosition="left"
                    aria-label="Close"
                  >
                    <i class="fa-utility fa-semibold fa-xmark"></i>
                  </button>
                </div>
                <div class="modal-subtitle"><strong>Quarantine Mode:</strong> ON</div>
                <ul>
                  <li>{{ application.name }}</li>
                </ul>
                <div class="notice-warning">
                  <i class="fa-solid fa-circle-exclamation"></i>
                  <p>
                    Launching a Recovery will restore your application in your recovery environment without the
                    safeguards of a Test.
                  </p>
                </div>
                <div class="modal-footer">
                  <button type="button" class="arpio-secondary-button" (click)="areYouSureDialog.close()">
                    No, Cancel Recovery
                  </button>
                  <button type="button" class="arpio-danger-button text-white" (click)="areYouSureDialog.close()">
                    Yes, I'm sure
                  </button>
                </div>
              </div>
            </ng-template>
          }
          <!-- Generic + integration modals (don't require an application) -->
          <modal-dialog #confirmActionDialog [dialogTemplate]="confirmActionTpl"></modal-dialog>
          <ng-template #confirmActionTpl>
            <confirm-action-dialog
              name="Mock resource"
              action="Delete"
              (onAction)="confirmActionDialog.close()"
              (onCancel)="confirmActionDialog.close()"
            ></confirm-action-dialog>
          </ng-template>
          <modal-dialog #updateAccessDialog [dialogTemplate]="updateAccessTpl"></modal-dialog>
          <ng-template #updateAccessTpl>
            <update-access-dialog
              [applicationType]="applicationTypeAws"
              [accessCheckResults]="mockAccessCheckResults"
              [sourceEndpointUri]="mockSourceEndpointUri"
              [targetEndpointUri]="mockTargetEndpointUri"
              (cancel)="updateAccessDialog.close()"
              (done)="updateAccessDialog.close()"
            ></update-access-dialog>
          </ng-template>
          <modal-dialog #editIntegrationDialog [dialogTemplate]="editIntegrationTpl"></modal-dialog>
          <ng-template #editIntegrationTpl>
            <edit-integration-dialog
              [integration]="mockIntegration"
              (cancel)="editIntegrationDialog.close()"
              (save)="editIntegrationDialog.close()"
            ></edit-integration-dialog>
          </ng-template>
          <!-- Inline copies of dialogs that live inside other components
          (support, account-retention-policy, issue-view children) so the
          test page can preview them without instantiating those parents. -->
          <modal-dialog #submitTicketDialog [dialogTemplate]="submitTicketTpl"></modal-dialog>
          <ng-template #submitTicketTpl>
            <div class="flex-left-column light-control-host">
              <div class="modal-header">
                <h3 class="panel-title">Open a ticket</h3>
                <button
                  type="button"
                  class="modal-close-btn"
                  (click)="submitTicketDialog.close()"
                  pTooltip="Close"
                  tooltipPosition="left"
                  aria-label="Close"
                >
                  <i class="fa-utility fa-semibold fa-xmark"></i>
                </button>
              </div>
              <div class="modal-subtitle">
                Tell us what's going on and we'll get back to you within 4 business hours.
              </div>
              <form class="support-form" [formGroup]="mockTicketFormModel">
                <fieldset style="grid-column: 1; grid-row: 1">
                  <label for="testYourEmail">Your email</label>
                  <input id="testYourEmail" class="arpio-input" type="email" formControlName="email" />
                </fieldset>
                <fieldset style="grid-column: 2; grid-row: 1">
                  <label for="testTicketPriority">Priority</label>
                  <div class="select-wrapper">
                    <select id="testTicketPriority" class="arpio-select full-width" formControlName="ticketPriority">
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>
                </fieldset>
                <fieldset style="grid-column: 1 / 3; grid-row: 2">
                  <label for="testTicketSubject">Subject</label>
                  <input id="testTicketSubject" class="arpio-input" type="text" formControlName="ticketSubject" />
                </fieldset>
                <fieldset style="grid-column: 1 / 3; grid-row: 3 / 5">
                  <label for="testTicketContent">Description</label>
                  <textarea id="testTicketContent" class="arpio-input" formControlName="ticketContent"></textarea>
                </fieldset>
              </form>
              <div class="modal-footer">
                <button type="button" class="arpio-secondary-button" (click)="submitTicketDialog.close()">
                  Cancel
                </button>
                <button type="button" class="arpio-button" (click)="submitTicketDialog.close()">Submit</button>
              </div>
            </div>
          </ng-template>
          <modal-dialog #confirmTicketDialog [dialogTemplate]="confirmTicketTpl"></modal-dialog>
          <ng-template #confirmTicketTpl>
            <div class="flex-left-column light-control-host">
              <div class="modal-header">
                <h3 class="panel-title">Ticket opened</h3>
                <button
                  type="button"
                  class="modal-close-btn"
                  (click)="confirmTicketDialog.close()"
                  pTooltip="Close"
                  tooltipPosition="left"
                  aria-label="Close"
                >
                  <i class="fa-utility fa-semibold fa-xmark"></i>
                </button>
              </div>
              <div class="ticket-confirmation-body" [innerHTML]="mockHubspotResponse"></div>
              <div class="modal-footer">
                <button type="button" class="arpio-secondary-button" (click)="confirmTicketDialog.close()">
                  Close
                </button>
              </div>
            </div>
          </ng-template>
          <modal-dialog #releaseRpsDialog [dialogTemplate]="releaseRpsTpl"></modal-dialog>
          <ng-template #releaseRpsTpl>
            <div class="flex-center-column full-width">
              <confirm-action-dialog
                action="Release"
                name="Recovery Points"
                [error]="null"
                (onCancel)="releaseRpsDialog.close()"
                (onAction)="releaseRpsDialog.close()"
                [busy]="false"
              >
                <div class="error margin-top">
                  Note: After the Recovery Points are released they are eligible for clean up and may be deleted by
                  Arpio.
                </div>
              </confirm-action-dialog>
            </div>
          </ng-template>
          <modal-dialog #confirmManualReplicationDialog [dialogTemplate]="confirmManualReplicationTpl"></modal-dialog>
          <ng-template #confirmManualReplicationTpl>
            <div class="flex-left-column light-control-host">
              <div class="modal-header">
                <h3 class="panel-title">Configuring Bucket Replication</h3>
                <button
                  type="button"
                  class="modal-close-btn"
                  (click)="confirmManualReplicationDialog.close()"
                  pTooltip="Close"
                  tooltipPosition="left"
                  aria-label="Close"
                >
                  <i class="fa-utility fa-semibold fa-xmark"></i>
                </button>
              </div>
              <div class="modal-subtitle">
                You're choosing to configure replication yourself. Here's what happens next:
              </div>
              <ol>
                <li>Arpio will create a new bucket in the recovery environment if it doesn't already exist.</li>
                <li>
                  Arpio will create an IAM role in the production environment with minimal permissions to replicate
                  objects to the recovery environment bucket.
                </li>
                <li>
                  Arpio will create a new issue in this console with the details necessary for you to configure
                  replication yourself.
                </li>
              </ol>
              <p>
                When you see the new issue here in Arpio it will be time to configure replication. Hang tight until
                then.
              </p>
              <div class="modal-footer">
                <button type="button" class="arpio-secondary-button" (click)="confirmManualReplicationDialog.close()">
                  CANCEL
                </button>
                <button type="button" class="arpio-button" (click)="confirmManualReplicationDialog.close()">
                  CONTINUE
                </button>
              </div>
            </div>
          </ng-template>
          <modal-dialog #confirmEbsSnapshotDialog [dialogTemplate]="confirmEbsSnapshotTpl"></modal-dialog>
          <ng-template #confirmEbsSnapshotTpl>
            <div class="flex-left-column light-control-host">
              <div class="modal-header">
                <h3 class="panel-title">Replication with EBS Snapshots</h3>
                <button
                  type="button"
                  class="modal-close-btn"
                  (click)="confirmEbsSnapshotDialog.close()"
                  pTooltip="Close"
                  tooltipPosition="left"
                  aria-label="Close"
                >
                  <i class="fa-utility fa-semibold fa-xmark"></i>
                </button>
              </div>
              <div class="modal-subtitle">
                You're choosing to replicate this server via EBS Snapshots. Here's what that means:
              </div>
              <ol>
                <li>
                  The Elastic Disaster Recovery service will not be used to replicate this server's data as it is
                  updated.
                </li>
                <li>
                  Instead, Arpio will take frequent snapshots of the server's disks and copy them to the recovery
                  environment.
                </li>
                <li>
                  During a recovery, this server will be re-created from these snapshots, and the very latest data from
                  the server may not be included.
                </li>
                <li>But you will not need to install an agent on the server, and your AWS bill will be lower.</li>
              </ol>
              <p>Choose Use Snapshots to proceed, or Cancel to go back.</p>
              <div class="modal-footer">
                <button type="button" class="arpio-secondary-button" (click)="confirmEbsSnapshotDialog.close()">
                  CANCEL
                </button>
                <button type="button" class="arpio-button" (click)="confirmEbsSnapshotDialog.close()">
                  USE SNAPSHOTS
                </button>
              </div>
            </div>
          </ng-template>
          <!-- Self-wrapped modals: these components own their own modal-dialog,
          so we just instantiate them and call .open() on the component. -->
          <app-new-role-modal #newRoleModal></app-new-role-modal>
          <app-edit-role-modal #editRoleModal></app-edit-role-modal>
          <app-new-group-modal #newGroupModal></app-new-group-modal>
          <app-edit-group-modal #editGroupModal></app-edit-group-modal>
          <app-edit-user-modal #editUserModal></app-edit-user-modal>
          <app-invite-users-modal #inviteUsersModal></app-invite-users-modal>
          <app-new-api-key-modal #newApiKeyModal></app-new-api-key-modal>
          <app-edit-api-key-modal #editApiKeyModal></app-edit-api-key-modal>
          <app-idp-modal #idpModal></app-idp-modal>
        </div>
      }
    </section>

    <!-- Resources Section -->
    <section id="resources-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('resources')">
        <h2>
          <i class="fa-solid fa-cube section-icon"></i>
          Resource Status
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['resources']"
          [class.fa-chevron-up]="expandedSections['resources']"
        ></i>
      </div>
      @if (expandedSections['resources']) {
        <div class="section-body">
          <p class="section-description">
            Mock resource statuses across all available status types. Pill labels swap based on context, so we render
            the table in both — Recovery (e.g. "Restoring", "Success") and Backup (e.g. "Backing Up", "Ready").
          </p>
          <h3 class="resource-status-subheader">Recovery context</h3>
          <app-resources-view
            [resources]="statusResources"
            [tabType]="RecoveryTab"
            [statusContext]="StatusContext.RECOVERY"
            [loading]="false"
          ></app-resources-view>
          <h3 class="resource-status-subheader">Backup context</h3>
          <app-resources-view
            [resources]="statusResources"
            [tabType]="BackupTab"
            [statusContext]="StatusContext.BACKUP"
            [loading]="false"
          ></app-resources-view>

          <h3 class="resource-status-subheader">Parent–child groups</h3>
          <p class="section-description">
            Expandable parents (clusters, DB clusters, ASGs) with linked children. The "Needs attention" rollup pill
            replaces a parent's own pill only when a <em>descendant</em> needs attention — but a parent whose
            <em>own</em> status is a trigger state shows its real status pill instead. Expand each row to see its
            children. Both tables use the same data; row names describe the scenario.
          </p>
          <ul class="scenario-legend section-description">
            <li>
              <strong>s1 AKS cluster (Failed)</strong> → shows <em>Failed</em>, not "Needs attention" (the bug fix)
            </li>
            <li><strong>s2 EKS cluster (Succeeded) + failed child</strong> → <em>Needs attention</em> + issue icon</li>
            <li><strong>s3 EKS cluster (Restoring) + failed child</strong> → <em>Needs attention</em></li>
            <li><strong>s4a AKS cluster (Not Processed)</strong> → own <em>Not Processed</em> pill</li>
            <li>
              <strong>s4b EKS cluster (Unsatisfied dependency)</strong> → own pill in <em>Recovery</em>, but
              <em>Needs attention</em> in <em>Backup</em> (recovery-only trigger)
            </li>
            <li><strong>s5 EKS cluster (No status) + failed child</strong> → <em>Needs attention</em> in both</li>
            <li><strong>s6 AKS cluster (Scan infected)</strong> → own pill in both contexts</li>
            <li>
              <strong>s7 Aurora cluster, all healthy</strong> → own <em>Success</em> pill, no rollup, no issue icon
            </li>
            <li>
              <strong>s8 EKS cluster (Succeeded) + in-flight children</strong> → promoted to <em>Restoring</em>, no
              rollup
            </li>
            <li>
              <strong>s9 Auto Scaling Group + failed instance</strong> → <em>Needs attention</em> (common non-K8s group)
            </li>
          </ul>
          <h4 class="resource-status-subheader">Recovery context</h4>
          <app-resources-view
            [resources]="parentChildResources"
            [tabType]="RecoveryTab"
            [statusContext]="StatusContext.RECOVERY"
            [loading]="false"
          ></app-resources-view>
          <h4 class="resource-status-subheader">Backup context</h4>
          <app-resources-view
            [resources]="parentChildResources"
            [tabType]="BackupTab"
            [statusContext]="StatusContext.BACKUP"
            [loading]="false"
          ></app-resources-view>
        </div>
      }
    </section>

    <!-- Shared tab-bar action-button row (ARP-7143). Consumed by both the
         Recovery and Backup screen sections via ngTemplateOutlet so the
         production button gating (per-phase visibility, disabled-with-tooltip
         states) lives in one place. Context: { flags: RecoveryScreenTabBarFlags }. -->
    <ng-template #tabBarActionsTpl let-flags="flags">
      <!-- Matches the post-ARP-7119 production model: per-phase buttons always
           render during in-flight ops and are disabled with `disabledActionReason`
           as their tooltip rather than collapsing the row to a spinner. See
           application.component.html tab-bar-actions + disabledActionReason. -->
      <button
        *ngIf="flags.showSandboxSettings"
        type="button"
        class="arpio-secondary-button"
        [disabled]="!!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        SANDBOX SETTINGS
      </button>
      <button
        *ngIf="flags.showTestButton"
        type="button"
        class="arpio-button"
        [disabled]="flags.testRecoverDisabled || !!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        <span class="button-label">TEST</span>
      </button>
      <button
        *ngIf="flags.showRecoverButton"
        type="button"
        class="arpio-danger-button"
        [disabled]="flags.testRecoverDisabled || !!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        <span class="button-label">LAUNCH RECOVERY</span>
      </button>
      <button
        *ngIf="flags.showUnquarantine"
        type="button"
        class="arpio-button"
        [disabled]="!!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        UNQUARANTINE
      </button>
      <button
        *ngIf="flags.showFailback"
        type="button"
        class="arpio-button"
        [disabled]="flags.failbackDisabled || !!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason || (flags.failbackDisabled ? failbackNoResourcesTooltip : null)"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        FAILBACK
      </button>
      <button
        *ngIf="flags.showConcludeRecovery"
        type="button"
        class="arpio-danger-button"
        [disabled]="!!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        CONCLUDE RECOVERY
      </button>
      <button
        *ngIf="flags.showConcludeTest"
        type="button"
        class="arpio-button"
        [disabled]="!!flags.disabledActionReason"
        [pTooltip]="flags.disabledActionReason"
        tooltipPosition="top"
        tooltipStyleClass="tooltip-long"
      >
        {{ flags.isEarlyStopTest ? 'CANCEL TEST' : 'CONCLUDE TEST' }}
      </button>
    </ng-template>

    <!-- Recovery screen previews (ARP-7143). Composed snapshots of the
         status bar + operation notice + sub-tab heading + resource table the user
         normally has to orchestrate real resources and a recovery to see. -->
    <section id="recovery-screen-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('recoveryScreen')">
        <h2>
          <i class="fa-solid fa-display section-icon"></i>
          Recovery screens
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['recoveryScreen']"
          [class.fa-chevron-up]="expandedSections['recoveryScreen']"
        ></i>
      </div>
      <div class="section-body" *ngIf="expandedSections['recoveryScreen']">
        <p class="section-description">
          Composed snapshots of the Recovery Environment and Failback sub-tabs. Pick a scenario to drive every part of
          the screen at once: status bar, operation-status notice, sub-tab heading, and resource table.
        </p>

        <label class="recovery-screen-picker">
          <span class="recovery-screen-picker-label">Scenario</span>
          <select class="arpio-select" (change)="setRecoveryScreenScenario($any($event.target).value)">
            <optgroup *ngFor="let group of recoveryScreenScenarioGroups" [label]="group.label">
              <option
                *ngFor="let scenario of group.scenarios"
                [value]="scenario.key"
                [selected]="scenario.key === recoveryScreenScenarioKey"
              >
                {{ scenario.label }}
              </option>
            </optgroup>
          </select>
        </label>
        <p class="recovery-screen-description">{{ recoveryScreenActiveScenario.description }}</p>

        <!-- Composed screen frame — markup mirrors application.component.html
             lines 106–569 so styling stays in sync via the global .tab-status-bar,
             .operation-status-notice, and .tab-heading classes.

             The #recoveryScreenDisplay template ref is the anchor for the
             capture-phase click blocker (see _attachScreenPreviewBlocker in
             the component). Every click / mousedown / Enter / Space inside
             this frame is short-circuited before reaching child handlers, so
             chip clicks, app-resources-view row clicks, filter pills, expand
             chevrons, etc. don't change state. Hover and PrimeNG tooltips
             still fire because pointer-events are otherwise unblocked. -->
        <div class="recovery-screen-frame" #recoveryScreenDisplay aria-label="Screen preview (read-only)">
          <!-- Main tab strip + action buttons. Surfaces the per-phase
               action-button enable/disable state without orchestrating a real
               recovery. Mirrors application.component.html lines 106–256. -->
          <div class="recovery-screen-tab-bar-wrapper">
            <div class="overview-tabs-bar" role="tablist">
              <button type="button" class="overview-tab" role="tab" [attr.aria-selected]="false">
                <i class="fa-solid fa-cloud-arrow-up mobile-tab-icon"></i>
                <span class="tab-label">Backup</span>
              </button>
              <button type="button" class="overview-tab active" role="tab" [attr.aria-selected]="true">
                <i class="fa-solid fa-cloud-arrow-down mobile-tab-icon"></i>
                <span class="tab-label">Recovery Environment</span>
              </button>
              <div class="tab-bar-actions">
                <ng-container
                  *ngTemplateOutlet="tabBarActionsTpl; context: { flags: recoveryScreenActiveScenario.tabBarFlags }"
                ></ng-container>
              </div>
            </div>
          </div>

          <!-- Auto-refresh indicator. Static mirror of application.component.html's
               .overview-refresh-bar — the self-refresh reassurance + hard-reload
               escape hatch that sits between the tab strip and the status bar. -->
          <div class="overview-refresh-bar">
            <div class="auto-refresh-indicator">
              <a class="reload-now-link" role="button" tabindex="0"
                ><i class="fa-utility fa-semibold fa-arrows-rotate"></i
              ></a>
              <span class="auto-refresh-text">Automatically updates every 60s </span>
            </div>
          </div>

          <!-- Sub-tab heading (Recovery / Failback chips). Only shown when the
               scenario is in FAILOVER and not quarantined, mirroring production's
               failback visibility — STANDBY / FAILOVER_TEST / CLEANUP / quarantined
               scenarios render no sub-tabs. -->
          <div class="tab-heading" *ngIf="screenShowsFailbackSubTabs">
            <div class="tab">
              <button
                type="button"
                [ngClass]="{ active: !screenIsFailbackTab }"
                (click)="switchRecoveryScreenTab('recovery')"
              >
                <h3 [ngClass]="{ inactive: screenIsFailbackTab }">Recovery</h3>
              </button>
              <button
                type="button"
                [ngClass]="{ active: screenIsFailbackTab }"
                (click)="switchRecoveryScreenTab('failback')"
              >
                <h3 [ngClass]="{ inactive: !screenIsFailbackTab }">Failback</h3>
              </button>
            </div>
          </div>

          <!-- Tab status bar. Same markup the existing tabStatusGroups loop uses. -->
          <div class="tab-status-bar" [ngClass]="screenStatus.state" aria-live="polite">
            <i *ngIf="screenStatus.state === 'indeterminate'" class="tab-status-icon fa-solid fa-spinner fa-spin"></i>
            <div class="tab-status-content">
              <span class="tab-status-text">
                <ng-container *ngIf="screenStatus.failedLink; else screenStatusPlain">
                  {{ screenStatus.failedLink.prefix }}
                  <a class="failed-resource-link" href="javascript:void(0)" role="button">
                    {{ screenStatus.failedLink.count }} resource<span *ngIf="screenStatus.failedLink.count !== 1"
                      >s</span
                    >
                  </a>
                  {{ screenStatus.failedLink.suffix }}
                </ng-container>
                <ng-template #screenStatusPlain>{{ screenStatus.text }}</ng-template>
              </span>
              <a
                class="tab-status-issues-link"
                href="javascript:void(0)"
                role="button"
                *ngIf="screenStatus.issues.length > 0"
              >
                Review and fix {{ screenStatus.issues.length }} issue<span *ngIf="screenStatus.issues.length !== 1"
                  >s</span
                >
              </a>
              <span class="tab-status-progress" *ngIf="screenStatus.progressTotal > 0">
                <span class="progress-track">
                  <span
                    class="progress-fill"
                    [style.width]="
                      screenStatus.progressCount >= screenStatus.progressTotal
                        ? '100%'
                        : 'min(' +
                          (screenStatus.progressCount / screenStatus.progressTotal) * 100 +
                          '%, calc(100% - 3px))'
                    "
                  ></span>
                </span>
                <span class="progress-label"
                  >({{ screenStatus.progressCount }} / {{ screenStatus.progressTotal }})</span
                >
              </span>
              <span class="tab-status-ratio" *ngIf="screenStatus.ratioText">{{ screenStatus.ratioText }}</span>
            </div>
          </div>

          <!-- Recovery operation-status notice. Mirrors the gating in
               application.component.html — only on the Recovery sub-tab, and only
               when the scenario provides operationNotice data (non-standby). -->
          <div
            class="operation-status-notice operation-status-notice--static"
            *ngIf="!screenIsFailbackTab && screenNotice"
          >
            <i class="fa-utility fa-semibold fa-circle-info"></i>
            <div class="operation-status-content">
              <span class="operation-status-summary">
                Applied recovery point is {{ screenNotice.recoveryPointTimeString }}.
                <ng-container *ngIf="screenNotice.showNetworkSandboxStatus">
                  Network sandbox is {{ screenNotice.networkSandboxStatus }}.
                  <ng-container *ngIf="screenNotice.showSandboxEventsLink">
                    <a href="javascript:void(0)" role="button">See most recent events.</a>
                  </ng-container>
                </ng-container>
                <ng-container *ngIf="screenNotice.showRansomwareRecoveryStatus">
                  Quarantine Mode is {{ screenNotice.ransomwareRecoveryStatus }}.
                </ng-container>
              </span>
              <div class="allowlist-status" *ngIf="screenNotice.domainAllowlistStatus">
                <span class="allowlist-text">{{ screenNotice.domainAllowlistStatus }}</span>
              </div>
              <div class="allowlist-status" *ngIf="screenNotice.whitelistStatus">
                <span class="allowlist-text">{{ screenNotice.whitelistStatus }}</span>
              </div>
            </div>
          </div>

          <!-- Failback operation notice. State-aware text mirrors the
               application.component.ts failbackOperationNoticeText getter. -->
          <div
            class="operation-status-notice operation-status-notice--static"
            *ngIf="screenIsFailbackTab && screenFailbackNoticeText"
          >
            <i class="fa-utility fa-semibold fa-circle-info"></i>
            <div class="operation-status-content">{{ screenFailbackNoticeText }}</div>
          </div>

          <app-resources-view
            [resources]="screenResources"
            [tabType]="screenTabType"
            [application]="screenApp"
            [loading]="false"
            [statusContext]="StatusContext.RECOVERY"
          ></app-resources-view>
        </div>
      </div>
    </section>

    <!-- Backup screen previews (ARP-7143). Backup-tab counterpart to the
         Recovery section above. Composition is simpler: main tab strip with
         Backup highlighted, status bar (BackupHealthStatus shape with
         priorBackupInfo + progress bar), resource table in BACKUP context.
         No sub-tabs, no operation notice — Backup tab renders neither. -->
    <section id="backup-screen-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('backupScreen')">
        <h2>
          <i class="fa-solid fa-cloud-arrow-up section-icon"></i>
          Backup screens
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['backupScreen']"
          [class.fa-chevron-up]="expandedSections['backupScreen']"
        ></i>
      </div>
      <div class="section-body" *ngIf="expandedSections['backupScreen']">
        <p class="section-description">
          Composed snapshots of the Backup tab. Pick a scenario to drive the status bar (creating / planning / last
          successful / failed) and the resource table with backup-context pill labels.
        </p>

        <label class="recovery-screen-picker">
          <span class="recovery-screen-picker-label">Scenario</span>
          <select class="arpio-select" (change)="setBackupScreenScenario($any($event.target).value)">
            <optgroup *ngFor="let group of backupScreenScenarioGroups" [label]="group.label">
              <option
                *ngFor="let scenario of group.scenarios"
                [value]="scenario.key"
                [selected]="scenario.key === backupScreenScenarioKey"
              >
                {{ scenario.label }}
              </option>
            </optgroup>
          </select>
        </label>
        <p class="recovery-screen-description">{{ backupScreenActiveScenario.description }}</p>

        <!-- Same #*Display ref pattern as Recovery: the capture-phase click
             blocker (TestPageComponent.ngAfterViewChecked) attaches here to
             keep this preview read-only without killing hover/tooltips. -->
        <div class="recovery-screen-frame" #backupScreenDisplay aria-label="Backup screen preview (read-only)">
          <!-- Main tab strip — Backup is active here. -->
          <div class="recovery-screen-tab-bar-wrapper">
            <div class="overview-tabs-bar" role="tablist">
              <button type="button" class="overview-tab active" role="tab" [attr.aria-selected]="true">
                <i class="fa-solid fa-cloud-arrow-up mobile-tab-icon"></i>
                <span class="tab-label">Backup</span>
              </button>
              <button type="button" class="overview-tab" role="tab" [attr.aria-selected]="false">
                <i class="fa-solid fa-cloud-arrow-down mobile-tab-icon"></i>
                <span class="tab-label">Recovery Environment</span>
              </button>
              <div class="tab-bar-actions">
                <ng-container
                  *ngTemplateOutlet="tabBarActionsTpl; context: { flags: backupScreenActiveScenario.tabBarFlags }"
                ></ng-container>
              </div>
            </div>
          </div>

          <!-- Backup tab status bar. Same markup as the Recovery section but
               also renders priorBackupInfo (Backup-only field on the status
               object). -->
          <div class="tab-status-bar" [ngClass]="backupScreenStatus.state" aria-live="polite">
            <i
              *ngIf="backupScreenStatus.state === 'indeterminate'"
              class="tab-status-icon fa-solid fa-spinner fa-spin"
            ></i>
            <div class="tab-status-content">
              <span class="tab-status-text">
                <ng-container *ngIf="backupScreenStatus.failedLink; else backupStatusPlain">
                  {{ backupScreenStatus.failedLink.prefix }}
                  <a class="failed-resource-link" href="javascript:void(0)" role="button">
                    {{ backupScreenStatus.failedLink.count }} resource<span
                      *ngIf="backupScreenStatus.failedLink.count !== 1"
                      >s</span
                    >
                  </a>
                  {{ backupScreenStatus.failedLink.suffix }}
                </ng-container>
                <ng-template #backupStatusPlain>{{ backupScreenStatus.text }}</ng-template>
              </span>
              <a
                class="tab-status-issues-link"
                href="javascript:void(0)"
                role="button"
                *ngIf="backupScreenStatus.issues.length > 0"
              >
                Review and fix {{ backupScreenStatus.issues.length }} issue<span
                  *ngIf="backupScreenStatus.issues.length !== 1"
                  >s</span
                >
              </a>
              <span class="tab-status-progress" *ngIf="backupScreenStatus.progressTotal > 0">
                <span class="progress-track">
                  <span
                    class="progress-fill"
                    [style.width]="
                      backupScreenStatus.progressCount >= backupScreenStatus.progressTotal
                        ? '100%'
                        : 'min(' +
                          (backupScreenStatus.progressCount / backupScreenStatus.progressTotal) * 100 +
                          '%, calc(100% - 3px))'
                    "
                  ></span>
                </span>
                <span class="progress-label"
                  >({{ backupScreenStatus.progressCount }} / {{ backupScreenStatus.progressTotal }})</span
                >
              </span>
              <span class="tab-status-ratio" *ngIf="backupScreenStatus.ratioText">{{
                backupScreenStatus.ratioText
              }}</span>
              <span class="tab-status-prior-info" *ngIf="backupScreenStatus.priorBackupInfo">{{
                backupScreenStatus.priorBackupInfo
              }}</span>
            </div>
          </div>

          <app-resources-view
            [resources]="backupScreenResources"
            [tabType]="BackupTab"
            [application]="backupScreenApp"
            [loading]="false"
            [statusContext]="StatusContext.BACKUP"
          ></app-resources-view>
        </div>
      </div>
    </section>

    <!-- Tab Status Bar — mock-fed replica of every tab-status-bar state across
    Backup Health, Recovery Environment, and Failback tabs. -->
    <section id="tab-status-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('tabStatus')">
        <h2>
          <i class="fa-solid fa-bars-progress section-icon"></i>
          Tab Status Bar
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['tabStatus']"
          [class.fa-chevron-up]="expandedSections['tabStatus']"
        ></i>
      </div>
      @if (expandedSections['tabStatus']) {
        <div class="section-body">
          <p class="section-description">
            Tab-status-bar variants grouped by tab and sync phase. Mirrors the markup that renders inside
            ApplicationComponent so styling stays in sync via the global <code>.tab-status-bar</code> styles in
            <code>styles.scss</code>.
          </p>
          @for (group of tabStatusGroups; track group) {
            <div class="tab-status-tab-group">
              <div class="tab-status-tab-group__header clickable" (click)="toggleSection(group.key)">
                <h3 class="tab-status-tab-group__title">{{ group.tab }}</h3>
                <i
                  class="fa-solid tab-status-tab-group__chevron"
                  [class.fa-chevron-down]="!expandedSections[group.key]"
                  [class.fa-chevron-up]="expandedSections[group.key]"
                ></i>
              </div>
              @if (expandedSections[group.key]) {
                <div class="tab-status-tab-group__body">
                  @for (phaseGroup of group.phases; track phaseGroup) {
                    <div class="tab-status-phase-group">
                      <h4 class="tab-status-phase-group__title">{{ phaseGroup.phase }}</h4>
                      <div class="tab-status-variant-list">
                        @for (variant of phaseGroup.variants; track variant) {
                          <div class="tab-status-variant">
                            <div class="tab-status-variant__label">{{ variant.label }}</div>
                            <div class="tab-status-bar" [ngClass]="variant.status.state" aria-live="polite">
                              @if (variant.status.state === 'indeterminate') {
                                <i class="tab-status-icon fa-solid fa-spinner fa-spin"></i>
                              }
                              <div class="tab-status-content">
                                <span class="tab-status-text">
                                  @if (variant.status.failedLink) {
                                    {{ variant.status.failedLink.prefix }}
                                    <!-- Pluralize inside the interpolation, not via a separate trailing-"s" element.
                                         The plural "s" in its own element leaves template whitespace (rendered as
                                         "resource s"), and Prettier reformats any single-line workaround back into
                                         that layout. Keep the anchor boundaries butted against the text. -->
                                    <a class="failed-resource-link" href="javascript:void(0)" role="button"
                                      >{{ variant.status.failedLink.count }}
                                      {{ variant.status.failedLink.count === 1 ? 'resource' : 'resources' }}</a
                                    >
                                    {{ variant.status.failedLink.suffix }}
                                  } @else {
                                    {{ variant.status.text }}
                                  }
                                </span>
                                @if (variant.status.issues.length > 0) {
                                  <a class="tab-status-issues-link" href="javascript:void(0)" role="button"
                                    >Review and fix {{ variant.status.issues.length }}
                                    {{ variant.status.issues.length === 1 ? 'issue' : 'issues' }}</a
                                  >
                                }
                                @if (variant.status.progressTotal > 0) {
                                  <span class="tab-status-progress">
                                    <span class="progress-track">
                                      <span
                                        class="progress-fill"
                                        [style.width]="
                                          variant.status.progressCount >= variant.status.progressTotal
                                            ? '100%'
                                            : 'min(' +
                                              (variant.status.progressCount / variant.status.progressTotal) * 100 +
                                              '%, calc(100% - 3px))'
                                        "
                                      ></span>
                                    </span>
                                    <span class="progress-label"
                                      >({{ variant.status.progressCount }} / {{ variant.status.progressTotal }})</span
                                    >
                                  </span>
                                }
                                @if (variant.status.ratioText) {
                                  <span class="tab-status-ratio">{{ variant.status.ratioText }}</span>
                                }
                                @if ($any(variant.status).priorBackupInfo) {
                                  <span class="tab-status-prior-info">{{ $any(variant.status).priorBackupInfo }}</span>
                                }
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </section>

    <!-- RPO Chart — mock-fed replica for exercising stale / empty / loading edge cases. -->
    <section id="chart-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('chart')">
        <h2>
          <i class="fa-solid fa-chart-line section-icon"></i>
          RPO Chart
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['chart']"
          [class.fa-chevron-up]="expandedSections['chart']"
        ></i>
      </div>
      @if (expandedSections['chart']) {
        <div class="section-body">
          <p class="section-description">
            Replica of the application banner's RPO chart with mock data. Toggle a scenario to drive different empty
            states and the chart's auto-escalating preset window logic.
          </p>
          <div class="chart-scenario-toggle">
            @for (scenario of chartScenarios; track scenario) {
              <button
                type="button"
                class="chart-scenario-btn"
                [class.active]="chartScenario === scenario.key"
                (click)="setChartScenario(scenario.key)"
                [title]="scenario.description"
              >
                {{ scenario.label }}
              </button>
            }
          </div>
          <div class="chart-preview-host">
            <!-- 460×115 frame matching production. null → loading; [] → "No RPO
              data yet"; length>0 → chart. Never show "No RPO data yet"
              before the fetch confirms an empty array. -->
            <div class="chart-preview-frame">
              @if (chartData !== null) {
                @if (chartData.length > 0) {
                  <rpo-chart [rpo]="chartRpoSeconds" [recoveryPointAttempts]="chartData"></rpo-chart>
                } @else {
                  <div class="chart-no-data">No RPO data yet</div>
                }
              } @else {
                <div class="chart-loading">
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  Loading chart…
                </div>
              }
            </div>
          </div>
        </div>
      }
    </section>

    <section id="stacksets-section" class="page-section">
      <div class="section-header clickable" (click)="toggleSection('stacksets')">
        <h2>
          <i class="fa-solid fa-layer-group section-icon"></i>
          StackSets Scenarios
        </h2>
        <i
          class="fa-solid section-chevron"
          [class.fa-chevron-down]="!expandedSections['stacksets']"
          [class.fa-chevron-up]="expandedSections['stacksets']"
        ></i>
      </div>
      @if (expandedSections['stacksets']) {
        <div class="section-body">
          <p class="section-description">
            Two independent axes: the <strong>scenario preset</strong> (input combinations driving each distinct UI
            variant) and the <strong>regen-job state</strong> (drives the polled gating overlay). Pick one of each to
            inspect any combination.
          </p>
          <div class="stacksets-scenario-controls">
            <label class="stacksets-scenario-picker">
              <span class="stacksets-scenario-picker-label">Scenario preset</span>
              <select class="arpio-select" (change)="setStacksetsScenarioPreset($any($event.target).value)">
                @for (group of stacksetsScenarioGroups; track group) {
                  <optgroup [label]="group.group">
                    @for (preset of group.presets; track preset) {
                      <option [value]="preset.key" [selected]="preset.key === stacksetsScenarioKey">
                        {{ preset.label }}
                      </option>
                    }
                  </optgroup>
                }
              </select>
            </label>
            <p class="stacksets-scenario-description">{{ stacksetsActivePreset.description }}</p>
            <label class="stacksets-scenario-picker">
              <span class="stacksets-scenario-picker-label">Regen job state</span>
              <select class="arpio-select" (change)="setStacksetsJobScenarioByKey($any($event.target).value)">
                @for (scenario of stacksetsJobScenarios; track scenario) {
                  <option [value]="scenario.key" [selected]="scenario.key === stacksetsJobScenario">
                    {{ scenario.label }}
                  </option>
                }
              </select>
            </label>
            @if (stacksetsJobScenarios[stacksetsJobScenarioIndex]; as scenario) {
              <p class="stacksets-scenario-description">
                {{ scenario.description }}
              </p>
            }
          </div>
          @if (stacksetsActivePreset.flow === 'setup') {
            <stacksets-instructions
              [arpioAccountId]="'test-account-id'"
              [regenerationJobId]="stacksetsActiveJobId"
              [migrationMode]="s_migrationMode"
              [sourceAccountId]="'111111111111'"
              [sourceRegion]="'us-east-1'"
              [targetAccountId]="'222222222222'"
              [targetRegion]="'us-west-2'"
              [sourceStackSetTemplateUrl]="'https://example.s3.amazonaws.com/source-stackset.yml'"
              [targetStackSetTemplateUrl]="'https://example.s3.amazonaws.com/target-stackset.yml'"
              [sourceTemplateBucket]="'example-bucket'"
              [sourceTemplateKey]="'source-template.yml'"
              [targetTemplateBucket]="'example-bucket'"
              [targetTemplateKey]="'target-template.yml'"
              [sourceStackName]="s_sourceStackName"
              [targetStackName]="s_targetStackName"
              [sourceManagedByStackSet]="s_sourceManagedByStackSet"
              [targetManagedByStackSet]="s_targetManagedByStackSet"
              [testingSource]="s_testingSource"
              [testingTarget]="s_testingTarget"
              [sourceError]="s_sourceError"
              [targetError]="s_targetError"
              [sourceTestCount]="s_sourceTestCount"
              [targetTestCount]="s_targetTestCount"
              (testAccess)="onStacksetsTestAccess($event)"
            ></stacksets-instructions>
          }
          @if (stacksetsActivePreset.flow === 'update') {
            <stacksets-update-instructions
              [arpioAccountId]="'test-account-id'"
              [regenerationJobId]="stacksetsActiveJobId"
              [sourceAccountId]="'111111111111'"
              [sourceRegion]="'us-east-1'"
              [targetAccountId]="'222222222222'"
              [targetRegion]="'us-west-2'"
              [sourceStackSetTemplateUrl]="'https://example.s3.amazonaws.com/source-stackset.yml'"
              [targetStackSetTemplateUrl]="'https://example.s3.amazonaws.com/target-stackset.yml'"
              [sourceManagedByStackSet]="s_sourceManagedByStackSet"
              [targetManagedByStackSet]="s_targetManagedByStackSet"
              [sourceAccessState]="s_sourceAccessState"
              [targetAccessState]="s_targetAccessState"
              [sourceTestCount]="s_sourceTestCount"
              [targetTestCount]="s_targetTestCount"
            ></stacksets-update-instructions>
          }
        </div>
      }
    </section>
  </div>
  <!-- Toast tester: fixed top-right trigger that fires every UiService toast
    variant on top of whatever screen state the reviewer has set up. -->
  <div class="toast-tester" (click)="onPanelClick($event)">
    <button
      type="button"
      class="toast-tester__button"
      [class.is-open]="toastMenuOpen"
      [attr.aria-expanded]="toastMenuOpen"
      aria-label="Trigger test toasts"
      (click)="toggleToastMenu($event)"
    >
      <i class="fa-solid fa-bread-slice"></i>
    </button>
    @if (toastMenuOpen) {
      <div class="toast-tester__panel">
        <div class="toast-tester__panel-header">Trigger a toast</div>
        @for (group of toastGroups; track group) {
          <div class="toast-tester__group">
            <div class="toast-tester__group-label">{{ group.label }}</div>
            @for (variant of group.variants; track variant) {
              <button type="button" class="toast-tester__option" (click)="fireToast($event, variant.fire)">
                {{ variant.label }}
              </button>
            }
          </div>
        }
      </div>
    }
  </div>
</ng-template>
